import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { planApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAdminRights, isOwner } from '../utils/roles';
import { buttonClass } from './settings/SettingsUI';
import { AlertTriangleIcon } from './icons/LucideIcons';

// Plan & billing, where the owner pays the bill or updates the card.
export const PAY_BILL_PATH = '/settings?tab=plan';

/**
 * App-wide past-due warning (QA B P1-5): while a plan bill is unpaid (the
 * plan, or its tracked-orders overage), owners and admins see at the top of
 * every page how many days are left before the shop closes, not only on Plan
 * & billing. GET /api/plan sends `pastDue` { graceEndsAt, daysLeft, source }
 * only then; Twigs and paying shops render nothing. The owner gets "Pay or
 * update card" (Plan & billing); an admin is told to ask the owner, who is
 * the only one who can pay.
 */
const PastDueBanner = () => {
  const { user } = useAuth();
  const owner = isOwner(user);
  const admin = hasAdminRights(user);
  const navigate = useNavigate();
  const [pastDue, setPastDue] = useState(null);

  useEffect(() => {
    if (!admin) return undefined;
    let cancelled = false;
    planApi.get()
      .then((r) => { if (!cancelled && r?.success) setPastDue(r.data?.pastDue || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [admin]);

  if (!admin || !pastDue) return null;
  const days = Math.max(0, Number(pastDue.daysLeft) || 0);
  const when = days <= 1 ? 'within a day' : `within ${days} days`;

  return (
    <div
      className="banner banner-warn items-start rounded-none sm:items-center"
      role="alert"
      data-testid="past-due-banner"
    >
      <AlertTriangleIcon size={16} className="mt-0.5 flex-shrink-0 sm:mt-0" />
      <p className="flex-1">
        <span className="font-semibold">Your last payment for your plan didn&apos;t go through.</span>
        {' '}
        {owner
          ? `Pay the bill or update your card ${when} to keep your shop open.`
          : `Ask your shop's owner to pay the bill ${when} to keep the shop open.`}
      </p>
      {owner && (
        <button type="button" onClick={() => navigate(PAY_BILL_PATH)} className={`${buttonClass.primary} shrink-0`}>
          Pay or update card
        </button>
      )}
    </div>
  );
};

export default PastDueBanner;
